<template>
    <div class="living-options-dwellings">
      <div class="row">
        <div class="col-12">
          <div class="dwellings">
            <h5>Dwellings</h5>
            <button class="btn btn-primary mb-4" @click="addDwelling()">Add Dwelling</button>
            <div class="mb-2">
              <input class="form-control" type="text" v-model="dwellingFilterText" placeholder="Filter dwellings">
            </div>
            <v-table
              :columns="[
                { label: 'Label', field: 'label'},
                { label: 'Builder', field: 'builderLabel'},
              ]"
              :rows="filteredDwellings"
              :show-active-toggle="activeUser.access.indexOf('live-toggle') !== -1"
              save-action="SAVE_DWELLING"
              @edit="activeDwellingId = $event._id"
              @clone="cloneDwelling($event)"
              @delete="deleteDwelling($event._id)"
            />
          </div>
        </div>
      </div>

      <modal-popup :isVisible="!!activeDwelling" @closeModal="closeModal">
        <div v-if="activeDwelling" class="modal-row-padding">
          <div class="col-12 mb-4">
            <div class="row">
              <div class="col-12">
                <h3>Editing {{ activeDwelling.label }}</h3>
              </div>
            </div>
          </div>
          <div class="col-6 mb-4">
            <div class="row">
              <div class="col-6 mb-4">
                Label
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.label"
                />
              </div>
              <div class="col-6 mb-4">
                Display Name
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.displayName"
                />
              </div>
              <div class="col-6 mb-4">
                Dwelling Type
                <select class="form-control" v-model="activeDwelling.type">
                  <option value="house">House</option>
                  <option value="townhouse">Townhouse</option>
                  <option value="apartment">Apartment</option>
                </select>
              </div>
              <div class="col-6 mb-4">
                Bedrooms
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.beds"
                />
              </div>
              <div class="col-6 mb-4">
                Bathrooms
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.baths"
                />
              </div>
              <div class="col-6 mb-4">
                Car Spaces
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.cars"
                />
              </div>
              <div class="col-6 mb-4">
                Dwelling Size (m<sup>2</sup>)
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="activeDwelling.size"
                />
              </div>               
              <div class="col-6 mb-4">
                Minimum Frontage Width (m)
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="activeDwelling.minimumFrontageWidth"
                />
              </div>               
              <div class="col-6 mb-4">
                Price
                <input 
                  type="text" 
                  class="form-control" 
                  :value="activeDwelling.price"
                  @input="event => activeDwelling.price = event.target.value.replace(/[^0-9.]/g, '')"
                />
              </div>               
              <div class="col-6 mb-4">
                Virtual Tour URL
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="activeDwelling.virtualTourUrl"
                />
              </div>               
              <div class="col-12 mb-4">
                Description
                <ck-editor
                  v-model="activeDwelling.description"
                  id="dwellingDescription"
                />
              </div>
              <div class="col-12 mb-4">
                Feature List
                <ck-editor
                  v-model="activeDwelling.featureList"
                  id="dwellingFeatureList"
                />
              </div>
              <div class="col-12 mb-4">
                Is visible on webfeed?
                <info-box>
                  If selected the dwelling will be visible in the dwelling listing page and on the webfeed.
                </info-box>
                <br>
                <toggle-button 
                  v-model="activeDwelling.isVisibleOnWebfeed" 
                  :color="{ checked: '#28a745', unchecked: '#b8b8b8' }"
                  :sync="true"
                  :width="30" 
                  :height="20"
                />
              </div>
            </div>
          </div>

          <div class="col-6 mb-4">
            <div class="row">
              <div v-if="activeDwelling.floorplanImages" class="col-12 mb-4">
                Floorplan Images
                <input-asset
                  v-model="activeDwelling.floorplanImages"
                  key="floorplan-images"
                  :multiple="true"
                />
              </div>
              
              <div v-if="activeDwelling.outlookImages" class="col-12 mb-4">
                Outlook Images
                <input-asset
                  v-model="activeDwelling.outlookImages"
                  key="outlook-images"
                  :multiple="true"
                />
              </div>
              <div v-if="activeDwelling.facadeImages" class="col-12 mb-4">
                Facade Images
                <input-asset
                  v-model="activeDwelling.facadeImages"
                  key="facade-images"
                  :multiple="true"
                />
              </div>
              <div v-if="activeDwelling.miscImages" class="col-12 mb-4">
                Misc Images
                <input-asset
                  v-model="activeDwelling.miscImages"
                  key="misc-images"
                  :multiple="true"
                />
              </div>
              <div class="col-12 mb-4">
                Gallery Images
                <input-asset
                  v-model="activeDwelling.galleryImages"
                  key="gallery-images"
                  :multiple="true"
                />
              </div>
              <div class="col-12 mb-4">
                Builder Label
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.builderLabel"
                />
              </div>
              <div class="col-12 mb-4">
                Builder Logo
                <input-asset
                  v-model="activeDwelling.builderImage"
                  key="builder-image"
                />
              </div>
              <div class="col-12 mb-4">
                House Design
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.houseDesign"
                />
              </div>
              <div class="col-12 mb-4">
                Colour Scheme
                <input
                  type="text"
                  class="form-control"
                  v-model="activeDwelling.colourScheme"
                />
              </div>
            </div>
          </div>

          <div class="col-12">
            <button class="btn btn-sm btn-outline-danger" @click="closeModal">Cancel</button>
            <button class="btn btn-sm btn-primary ml-1" @click="saveDwelling(activeDwelling)">Save</button>
          </div>
        </div>             
      </modal-popup>      
    </div>     
</template>
<script>
import _ from 'lodash'
import CkEditor from '@/components/CKEditor'
import ImportDot from '@/components/ImportDot'
import InfoBox from '@/components/InfoBox'
import InputAsset from '@/components/InputAsset'
import InputMultiSelect from '@/components/InputMultiSelect'
import ModalPopup from '@/components/ModalPopup'
import VTable from '@/components/VTable'

export default {
  components: { CkEditor, ImportDot, InfoBox, InputAsset, InputMultiSelect, ModalPopup, VTable },
  data () {
    return {
      activeDwellingId: null,
      dwellingFilterText: '',
    }
  },

  computed: {
    activeDwelling () {
      return _.find(this.dwellings, dwelling => {
        return dwelling._id === this.activeDwellingId
      })
    },

    activeUser () {
      return this.$store.getters.user
    },

    filteredDwellings () {
      return _.filter(this.dwellings, dwelling => {
        return dwelling.label.toLowerCase().includes(this.dwellingFilterText.toLowerCase())
      })
    },

    dwellings () {
      return this.$store.getters.dwellings
    }
  },

  methods: {
    async addDwelling () {
      const { _id } = await this.$store.dispatch('ADD_NEW_DWELLING')
      this.activeDwellingId = _id
    },

    async cloneDwelling (dwelling) {
      const { _id } = await this.$store.dispatch('CLONE_DWELLING', dwelling)
      this.activeDwellingId = _id
    },

    closeModal () {
      this.activeDwellingId = null
      this.$store.dispatch('FETCH_DWELLINGS')
    },

    deleteDwelling (id) {
      if (confirm('Are you sure?')) {
        this.$store.dispatch('DELETE_DWELLING', id)
      }
    },

    saveDwelling (dwelling) {
      this.$store.dispatch('SAVE_DWELLING', dwelling)
      this.activeDwellingId = null
    },
  },

  created () {
    this.$store.dispatch('FETCH_ASSETS')
    this.$store.dispatch('FETCH_DWELLINGS')
  }
}
</script>